import { BadgeCheck, ShieldAlert, Sparkles } from 'lucide-react';
import { t, useLanguage } from '@/app/utils/i18n';
import type { AqlGrowthDashboardPoint } from '@/app/services/growth-service';

interface MeasurementQualityPanelProps {
  series: AqlGrowthDashboardPoint[];
  loading?: boolean;
}

interface QualitySummary {
  confidence: number | null;
  uniformity: number | null;
  hasEnoughSample: boolean | null;
  warningLevel: 'normal' | 'warning' | 'critical';
  sampleCount: number | null;
  rejectedCount: number | null;
}

const formatPct = (value: number | null) =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;

const computeSummary = (series: AqlGrowthDashboardPoint[]): QualitySummary => {
  if (!series.length) {
    return {
      confidence: null,
      uniformity: null,
      hasEnoughSample: null,
      warningLevel: 'normal',
      sampleCount: null,
      rejectedCount: null
    };
  }
  const latest = series[series.length - 1];
  return {
    confidence: latest.confidence,
    uniformity: latest.uniformity_score,
    hasEnoughSample: latest.has_enough_sample,
    warningLevel: latest.warning_level ?? 'normal',
    sampleCount: latest.sample_count,
    rejectedCount: latest.rejected_count
  };
};

export function MeasurementQualityPanel({ series, loading }: MeasurementQualityPanelProps) {
  const language = useLanguage();
  const tr = (key: string) => t('vision', key, language);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="h-5 w-44 animate-pulse rounded bg-muted/70" />
        <div className="mt-4 h-24 animate-pulse rounded bg-muted/40" />
      </div>
    );
  }

  const summary = computeSummary(series);

  const alertTone =
    summary.warningLevel === 'critical'
      ? 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-100'
      : summary.warningLevel === 'warning'
        ? 'border-amber-400/40 bg-amber-500/10 text-amber-700 dark:text-amber-100'
        : 'border-emerald-500/35 bg-emerald-500/10 text-emerald-700 dark:text-emerald-100';

  const alertCopy =
    summary.warningLevel === 'critical'
      ? tr('qualityCriticalCopy')
      : summary.warningLevel === 'warning'
        ? tr('qualityWarningCopy')
        : tr('qualityNormalCopy');

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">{tr('measurementConfidence')}</h3>
          <p className="text-xs text-muted-foreground">
            {tr('measurementConfidenceSubtitle')}
          </p>
        </div>
        <Sparkles className="h-4 w-4 text-muted-foreground" />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl border border-border bg-muted/40 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('confidence')}</p>
          <p className="mt-1 text-xl font-bold text-foreground">{formatPct(summary.confidence)}</p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('cohortUniformity')}</p>
          <p className="mt-1 text-xl font-bold text-foreground">{formatPct(summary.uniformity)}</p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('validSample')}</p>
          <p className="mt-1 text-xl font-bold text-foreground">
            {summary.sampleCount ?? '—'}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('rejectedMeasurements')}</p>
          <p className="mt-1 text-xl font-bold text-foreground">
            {summary.rejectedCount ?? '—'}
          </p>
        </div>
      </div>

      {summary.hasEnoughSample === false ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-100">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{tr('insufficientSampleSize')}</p>
        </div>
      ) : null}

      <div className={`mt-3 flex items-start gap-2 rounded-lg border p-3 text-xs ${alertTone}`}>
        <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <p>{alertCopy}</p>
      </div>
    </div>
  );
}
